import { ReactElement } from 'react';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { currentUser as CurrentUserQuery } from './__generated__/currentUser';

const currentUserQuery = gql`
  query currentUser {
    currentUserV2 {
      email
      fullName
      id
      imageUrl
      isDeveloper
      isStaff
      isMasquerade
      switches
      currentAccountId
    }

    isAuthenticatedV2

    accounts {
      companyAccountEndDate
      currentUserRole
      hasBeenInCompaniesTrial
      companiesTrialEndDate
      id
      isChatEligible
      isTrialing
      isTrialingCompanies
      teamAccountEndDate
    }
  }
`;

const useCurrentUser = () => {
  const { data, loading } = useQuery<CurrentUserQuery>(currentUserQuery, {
    ssr: false,
  });

  const { currentUserV2, isAuthenticatedV2, accounts } = data || {};
  const {
    email,
    fullName,
    id,
    imageUrl,
    isDeveloper,
    isStaff,
    switches,
    currentAccountId,
  } = currentUserV2 || {};

  const accountId = (id && parseInt(id, 10)) || null;
  const isLoggedIn = Boolean(isAuthenticatedV2);
  const isMasquerade = Boolean(currentUserV2?.isMasquerade);

  const companyTrialAccount =
    accounts && accounts.find(account => !!account.companiesTrialEndDate);

  const companiesTrialEndDate =
    companyTrialAccount && companyTrialAccount.companiesTrialEndDate;

  const currentAccount =
    accounts &&
    currentAccountId &&
    accounts.find(account => account.id.toString() === currentAccountId);

  const currentUser = {
    accountId,
    currentAccount,
    email,
    fullName,
    imageUrl,
    isDeveloper: Boolean(isDeveloper),
    isLoggedIn,
    isStaff: Boolean(isStaff),
    isTrialingCompanies: Boolean(
      accounts?.some((account: any) => account?.isTrialingCompanies)
    ),
    companiesTrialEndDate,
    switches,
  };

  return {
    currentUser,
    isMasquerade,
    isAuthenticated: isLoggedIn,
    isLoading: loading,
  };
};

/**
 * This is exposed for _app.js and other legacy uses you'll most likely want to
 * leverage the hook that is located above.
 */
export const WithCurrentUser = (props: {
  children: (result: ReturnType<typeof useCurrentUser>) => ReactElement;
}) => {
  const currentUserResult = useCurrentUser();

  return props.children(currentUserResult);
};

export default useCurrentUser;
